/* Custom elements for the pdp-new sections. The file is included by more
   than one section, so every definition is guarded. */

/* Kur-Auswahl option cards: swaps the selected variant on the product form
   and updates price, unit price and CTA state; the selected card's info
   bullets show via CSS on aria-pressed (Figma 348-39). */
if (!customElements.get('pdp-variant-picker')) {
  customElements.define(
    'pdp-variant-picker',
    class extends HTMLElement {
      connectedCallback() {
        const json = this.querySelector('[data-variants-json]');
        if (!json) return;
        this.variants = JSON.parse(json.textContent);
        this.addEventListener('click', this.onClick.bind(this));
      }

      onClick(event) {
        const option = event.target.closest('.pdp-variants__option');
        if (!option) return;
        const variant = this.variants.find((v) => String(v.id) === option.dataset.variantId);
        if (!variant) return;

        this.querySelectorAll('.pdp-variants__option').forEach((button) => {
          button.setAttribute('aria-pressed', button === option ? 'true' : 'false');
        });

        const form = document.getElementById(this.dataset.formId);
        if (form) {
          const idInput = form.querySelector('input[name="id"]');
          idInput.value = variant.id;
          idInput.disabled = false;
        }

        const section = this.closest('.pdp-new') || document;
        const price = section.querySelector('[data-pdp-price]');
        if (price) price.textContent = variant.price_formatted;
        const comparePrice = section.querySelector('[data-pdp-compare-price]');
        if (comparePrice) {
          comparePrice.textContent = variant.compare_at_price_formatted || '';
          comparePrice.hidden = !variant.compare_at_price_formatted;
        }
        const unitPrice = section.querySelector('[data-pdp-unit-price]');
        if (unitPrice) {
          unitPrice.textContent = variant.unit_price_formatted || '';
          unitPrice.hidden = !variant.unit_price_formatted;
        }
        const shippingNote = section.querySelector('[data-shipping-note]');
        if (shippingNote) {
          const free = variant.price >= Number(shippingNote.dataset.threshold);
          shippingNote.querySelector('[data-shipping-note-free]').hidden = !free;
          shippingNote.querySelector('[data-shipping-note-paid]').hidden = free;
        }
        const cta = section.querySelector('[data-pdp-cta]');
        if (cta) {
          cta.disabled = !variant.available;
          cta.textContent = variant.available ? cta.dataset.labelAvailable : cta.dataset.labelSoldOut;
        }
      }
    }
  );
}

/* Beschreibung / Inhaltsstoffe tabs (Figma 222-32, state 201-138). */
if (!customElements.get('pdp-tabs')) {
  customElements.define(
    'pdp-tabs',
    class extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
        this.tabs.forEach((tab) => tab.addEventListener('click', () => this.select(tab)));
      }

      select(selected) {
        this.tabs.forEach((tab) => tab.setAttribute('aria-selected', tab === selected ? 'true' : 'false'));
        this.panels.forEach((panel) => {
          panel.hidden = panel.id !== selected.getAttribute('aria-controls');
        });
      }
    }
  );
}

/* Mobile media slider with counter (Figma 114-468); on desktop the track
   becomes a static grid and the controls are hidden via CSS. */
if (!customElements.get('pdp-gallery')) {
  customElements.define(
    'pdp-gallery',
    class extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.pdp-gallery__track');
        if (!this.track) return;
        this.slides = Array.from(this.track.children);
        this.dots = Array.from(this.querySelectorAll('[data-slide-index]'));
        this.track.addEventListener('scroll', () => this.onScroll(), { passive: true });
        this.querySelectorAll('[data-slide-dir]').forEach((button) => {
          button.addEventListener('click', () => {
            const direction = button.dataset.slideDir === 'next' ? 1 : -1;
            this.track.scrollBy({ left: direction * this.track.clientWidth, behavior: 'smooth' });
          });
        });
        this.dots.forEach((dot) => {
          dot.addEventListener('click', () => {
            this.track.scrollTo({ left: Number(dot.dataset.slideIndex) * this.track.clientWidth, behavior: 'smooth' });
          });
        });
      }

      onScroll() {
        if (this.slides.length === 0) return;
        const index = Math.min(Math.round(this.track.scrollLeft / this.track.clientWidth), this.slides.length - 1);
        this.dots.forEach((dot, i) => dot.toggleAttribute('aria-current', i === index));
      }
    }
  );
}

/* Upsell fallback: without a selected collection the section carries a
   data-url to the product recommendations endpoint, refetches itself and
   swaps in the server-rendered cards (recommendations.products). */
if (!customElements.get('pdp-recommendations')) {
  customElements.define(
    'pdp-recommendations',
    class extends HTMLElement {
      connectedCallback() {
        if (!this.dataset.url) return;
        fetch(this.dataset.url)
          .then((response) => response.text())
          .then((text) => {
            const html = new DOMParser().parseFromString(text, 'text/html');
            const fresh = html.querySelector('pdp-recommendations');
            if (fresh && fresh.querySelector('.pdp-upsell-card')) {
              this.innerHTML = fresh.innerHTML;
            }
          })
          .catch((error) => console.error(error));
      }
    }
  );
}

/* Horizontal card carousel with desktop arrows (Figma 201-143, 201-200). */
if (!customElements.get('pdp-carousel')) {
  customElements.define(
    'pdp-carousel',
    class extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.pdp-carousel__track');
        if (!this.track) return;
        this.querySelectorAll('[data-scroll-dir]').forEach((button) => {
          button.addEventListener('click', () => {
            const card = this.track.firstElementChild;
            const step = card ? card.offsetWidth + 20 : this.track.clientWidth;
            const direction = button.dataset.scrollDir === 'next' ? 1 : -1;
            this.track.scrollBy({ left: direction * step, behavior: 'smooth' });
          });
        });
        this.track.addEventListener('scroll', () => this.updateArrows(), { passive: true });
        window.addEventListener('resize', () => this.updateArrows(), { passive: true });
        this.updateArrows();
      }

      updateArrows() {
        const maxScroll = this.track.scrollWidth - this.track.clientWidth;
        const prev = this.querySelector('[data-scroll-dir="prev"]');
        const next = this.querySelector('[data-scroll-dir="next"]');
        if (prev) prev.disabled = this.track.scrollLeft <= 0;
        if (next) next.disabled = this.track.scrollLeft >= maxScroll - 1;
      }
    }
  );
}

/* Adds every routine variant to the cart in one request and hands the result
   to the theme's pubsub and the app cart drawer, mirroring goodie.js. */
if (!customElements.get('pdp-routine-add')) {
  customElements.define(
    'pdp-routine-add',
    class extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('button');
        this.error = this.querySelector('[role="alert"]');
        if (!this.button) return;
        this.onClick = this.onClick.bind(this);
        this.button.addEventListener('click', this.onClick);
      }

      disconnectedCallback() {
        this.button?.removeEventListener('click', this.onClick);
      }

      async onClick() {
        const items = this.dataset.items.split(',').map((id) => ({ id: Number(id), quantity: 1 }));
        const sections = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections } }));
        this.button.disabled = true;
        this.button.setAttribute('aria-busy', 'true');
        this.error.hidden = true;
        try {
          const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items, sections })
          });
          const result = await response.json();
          if (!response.ok) {
            this.error.textContent = result.description || result.message;
            this.error.hidden = false;
            return;
          }
          const cart = await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();
          cart.sections = result.sections;
          window.theme?.pubsub?.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'pdp-routine-add', cart });
          if (typeof window.upcartRefreshCart === 'function') {
            window.upcartRefreshCart();
            window.upcartOpenCart?.();
          } else {
            document.querySelector('cart-drawer')?.show(this.button);
          }
        } finally {
          this.button.disabled = false;
          this.button.removeAttribute('aria-busy');
        }
      }
    }
  );
}
